import type { AccessibleClient, LoginCredentials, Token, User } from '../types/auth';
import { redirectToLogin } from '../lib/url';

const API_BASE = `${import.meta.env.VITE_API_URL || 'http://localhost:8001'}/api/v1`;

let refreshToken: string | null = localStorage.getItem('refresh_token');
let isRefreshing = false;
let refreshPromise: Promise<string> | null = null;

function getAccessToken(): string | null {
  return localStorage.getItem('auth_token');
}

function setTokens(accessToken: string, newRefreshToken?: string) {
  localStorage.setItem('auth_token', accessToken);
  if (newRefreshToken) {
    refreshToken = newRefreshToken;
    localStorage.setItem('refresh_token', newRefreshToken);
  }
}

export function clearTokens() {
  localStorage.removeItem('auth_token');
  localStorage.removeItem('refresh_token');
  refreshToken = null;
}

/**
 * Handler unique d'expiration de session (401), centralisé pour tous les
 * services. À appeler après qu'authFetch a déjà tenté le refresh. Absorbe le
 * cas le plus complet :
 *  - X-Session-Invalidated (#501) : un admin a modifié les accès du user →
 *    flag localStorage pour que LoginPage affiche un toast explicite.
 * Purge les tokens, redirige vers /login (préfixe-aware) et coupe la chaîne
 * via un throw (type de retour `never`). Avant centralisation, ce bloc était
 * dupliqué dans 8 sites et seul apiClient gérait X-Session-Invalidated.
 */
export function handleSessionExpired(response: Response): never {
  if (response.headers.get('X-Session-Invalidated') === '1') {
    localStorage.setItem('session_invalidated', '1');
  }
  clearTokens();
  redirectToLogin();
  throw new Error('Session expirée');
}

export async function login(credentials: LoginCredentials): Promise<Token> {
  const response = await fetch(`${API_BASE}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(credentials),
  });

  if (!response.ok) {
    if (response.status === 429) {
      throw new Error('Trop de tentatives. Veuillez réessayer dans quelques instants.');
    }
    const error = await response.json();
    throw new Error(error.detail || 'Erreur de connexion');
  }

  const token: Token = await response.json();
  setTokens(token.access_token, token.refresh_token);
  return token;
}

export async function refresh(): Promise<string> {
  if (!refreshToken) {
    throw new Error('No refresh token');
  }

  const response = await fetch(`${API_BASE}/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: refreshToken }),
  });

  if (!response.ok) {
    clearTokens();
    throw new Error('Session expirée');
  }

  const token: Token = await response.json();
  setTokens(token.access_token, token.refresh_token);
  return token.access_token;
}

/**
 * Performs an authenticated fetch with automatic token refresh on 401.
 */
export async function authFetch(url: string, options: RequestInit = {}): Promise<Response> {
  const token = getAccessToken();
  const headers = new Headers(options.headers);
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  let response = await fetch(url, { ...options, headers });

  if (response.status === 401) {
    // PROD1.4.7 #501 — session invalidée par un admin : court-circuit refresh
    // (les refresh tokens du user sont déjà révoqués côté serveur) et propage
    // le flag jusqu'à LoginPage via localStorage.
    if (response.headers.get('X-Session-Invalidated') === '1') {
      localStorage.setItem('session_invalidated', '1');
      clearTokens();
      // Redirect immédiat (avant que l'erreur ne remonte à l'appelant) pour
      // qu'AuthContext + écran courant soient remplacés par LoginPage qui lit
      // le flag localStorage. handleResponse ne sera jamais atteint car le
      // throw qui suit coupe la chaîne avant `await response.json()`.
      // endsWith (et non startsWith) pour rester correct sous sous-chemin :
      // pathname = '/dokimmo/login' ne « startsWith » pas '/login'.
      if (typeof window !== 'undefined' && !window.location.pathname.endsWith('/login')) {
        redirectToLogin();
      }
      throw new Error('Session invalidée');
    }
    if (refreshToken) {
      // Deduplicate concurrent refresh calls
      if (!isRefreshing) {
        isRefreshing = true;
        refreshPromise = refresh().finally(() => {
          isRefreshing = false;
          refreshPromise = null;
        });
      }

      try {
        const newToken = await refreshPromise!;
        headers.set('Authorization', `Bearer ${newToken}`);
        response = await fetch(url, { ...options, headers });
      } catch {
        clearTokens();
        throw new Error('Session expirée');
      }
    }
  }

  return response;
}

export async function logoutBackend(): Promise<void> {
  const token = getAccessToken();
  if (refreshToken && token) {
    try {
      await fetch(`${API_BASE}/auth/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
    } catch {
      // Best-effort logout on backend
    }
  }
  clearTokens();
}

export async function getMe(): Promise<User> {
  const response = await authFetch(`${API_BASE}/auth/me`);
  if (!response.ok) {
    throw new Error('Session invalide');
  }
  return response.json();
}

export async function selectContext(clientId: string): Promise<Token> {
  const response = await authFetch(`${API_BASE}/auth/select-context`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ client_id: clientId }),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Erreur lors de la sélection du contexte');
  }
  const token: Token = await response.json();
  setTokens(token.access_token, token.refresh_token);
  return token;
}

export async function switchContext(clientId: string): Promise<Token> {
  const response = await authFetch(`${API_BASE}/auth/switch-context`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ client_id: clientId }),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Erreur lors du changement de contexte');
  }
  const token: Token = await response.json();
  setTokens(token.access_token, token.refresh_token);
  return token;
}

export async function getAvailableClients(): Promise<AccessibleClient[]> {
  const response = await authFetch(`${API_BASE}/auth/available-clients`);
  if (!response.ok) {
    throw new Error('Erreur lors de la récupération des clients');
  }
  return response.json();
}

export async function updateMe(data: { full_name?: string; email?: string }): Promise<User> {
  const response = await authFetch(`${API_BASE}/auth/me`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Erreur lors de la mise à jour');
  }
  return response.json();
}

export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  const response = await authFetch(`${API_BASE}/auth/me/password`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Erreur lors du changement de mot de passe');
  }
}
